import React, { type FC } from 'react';
import { Building2, MapPin, Users } from 'lucide-react';
import { CardShell } from './card-shell';
import type { ToolCardProps } from './types';
import { safeGet, safeArray } from './types';

export const CompanyListCard: FC<ToolCardProps> = ({ result }) => {
  const companies = safeArray<Record<string, unknown>>(result, 'companies');
  const total = safeGet<number>(result, 'total') ?? companies.length;

  if (companies.length === 0) {
    return (
      <CardShell>
        <div style={{ padding: 18, fontSize: 13, color: 'var(--text-tertiary)' }}>
          No companies found. You can create one by telling me the company name and details.
        </div>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 0 }}>
        {companies.map((company, i) => {
          const id = safeGet<string>(company, 'id') ?? `${i}`;
          const name = safeGet<string>(company, 'name') ?? 'Unnamed';
          const location = safeGet<string>(company, 'location');
          const industry = safeGet<string>(company, 'industry');
          const employeeCount = safeGet<string>(company, 'employeeCount');
          const logoUrl = safeGet<string>(company, 'logo');

          return (
            <div
              key={id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 0',
                borderTop: i > 0 ? '1px solid var(--gem-outline-variant)' : undefined,
              }}
            >
              {/* Company logo */}
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'rgba(0,0,0,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-tertiary)',
                  flexShrink: 0,
                  overflow: 'hidden',
                }}
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={name}
                    referrerPolicy="no-referrer"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Building2 size={18} strokeWidth={1.6} />
                )}
              </div>

              {/* Details */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  {location && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <MapPin size={10} /> {location}
                    </span>
                  )}
                  {location && industry && <span style={{ opacity: 0.5 }}>&middot;</span>}
                  {industry && <span>{industry}</span>}
                  {(location || industry) && employeeCount && <span style={{ opacity: 0.5 }}>&middot;</span>}
                  {employeeCount && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Users size={10} /> {employeeCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {total > 0 && (
        <div style={{ padding: '0 16px 12px', fontSize: 12, color: 'var(--text-tertiary)' }}>
          {total} compan{total !== 1 ? 'ies' : 'y'}
        </div>
      )}
    </CardShell>
  );
};
